import { openDB } from "idb";

export abstract class ServiceSessionStorageBase {
  protected readonly storeName: string;
  protected readonly databaseName: string;

  constructor(storeName: string, databaseName: string) {
    this.storeName = storeName;
    this.databaseName = databaseName;
  }

  protected async initDB(): Promise<any> {
    const storeName = this.storeName;
    return await openDB(this.databaseName, 1, {
      upgrade(db) {
        db.createObjectStore(storeName);
      },
    });
  }

  protected async saveDataDB(key: string, value: any): Promise<void> {
    const db = await this.initDB();
    const tx = db.transaction(this.storeName, "readwrite");
    tx.store.put(value, key);
    await tx.done;
  }

  protected async getDataDB(key: string): Promise<any> {
    const db = await this.initDB();
    return await db.get(this.storeName, key);
  }

  protected async saveDataDbInChunks(
    key: string,
    value: any,
    chunkSize = 2_000_000,
  ): Promise<void> {
    const db = await this.initDB();
    const transaction = db.transaction(this.storeName, "readwrite");
    const store = transaction.objectStore(this.storeName);

    // store metadata with chunk count
    const totalChunks = Math.ceil(JSON.stringify(value).length / chunkSize);
    await store.put(
      {
        type: "metadata",
        totalChunks,
        originalKey: key,
      },
      `${key}_meta`,
    );

    // store each chunk
    const dataString = JSON.stringify(value);
    for (let i = 0; i < totalChunks; i++) {
      const chunk = dataString.substr(i * chunkSize, chunkSize);
      await store.put(
        {
          type: "chunk",
          chunkIndex: i,
          data: chunk,
          originalKey: key,
        },
        `${key}_chunk_${i}`,
      );
    }
  }

  protected async getDataDbByChunks(key: string): Promise<any> {
    const db = await this.initDB();

    const transaction = db.transaction(this.storeName, "readonly");
    const store = transaction.objectStore(this.storeName);

    const metadata = await store.get(`${key}_meta`);

    if (!metadata) {
      return null;
    }

    // get all chunks
    const chunks: any[] = [];
    for (let i = 0; i < metadata.totalChunks; i++) {
      const chunk = await store.get(`${key}_chunk_${i}`);
      chunks.push(chunk);
    }

    return JSON.parse(chunks.map((x) => x.data).join(""));
  }

  protected async deleteDataDB(key: string): Promise<void> {
    const db = await this.initDB();
    const tx = db.transaction(this.storeName, "readwrite");
    const st = tx.store;

    // delete chunks data
    const range = IDBKeyRange.bound(`${key}_`, `${key}_\uffff`);
    for (
      let cursor = await st.openCursor(range);
      cursor;
      cursor = await cursor.continue()
    ) {
      cursor.delete();
    }

    st.delete(key);
    await tx.done;
  }
}
